import type { Diagnosis } from '@seen/core/models/conditions/Diagnosis'
import {
  createReferenceVersionUrl,
  getReferenceString,
} from '@seen/core/references'

// The frozen snapshot a condition @-mention pins on pick (design §O.3,
// reimplementing #13981). Two frozen fields:
//   - `reference`: a VERSIONED reference (`Condition/<id>/_history/<v>`) so the
//     chip names the exact condition version the author saw, not whatever the
//     condition later becomes. Falls back to the unversioned reference only when
//     the resource has no persisted versionId yet.
//   - `name`: the condition's display name frozen at pick time (`code.text ??
//     title`), so a later rename of the condition does not silently rewrite the
//     note's prose.
export interface ConditionMentionSnapshot {
  reference: string
  name: string
}

export interface ConditionMentionSnapshotOptions {
  /*
   * Whether the chip pins the condition's version. True everywhere a pick is
   * itself the clinical act — the note editor, where the encounter link is
   * written in the same breath — which is #13981's case and the default.
   *
   * FALSE inside an AI suggestion's widget, where the pick is a draft edit of a
   * proposal that has not been accepted. There a version-pinned reference is
   * not merely unnecessary but rejected: a versioned reference is the accept
   * pipeline's "linkage already done" marker, so the content gate refuses to
   * INSERT one (see validateSuggestionContent), and a pinned chip would make the
   * suggestion unacceptable. Accept pins the version itself, through
   * applyConditionMentionAccept's returned patch, at the moment linkage actually
   * completes — which is what the marker is supposed to mean.
   */
  pinVersion: boolean
}

export function getConditionMentionSnapshot(
  diagnosis: Diagnosis,
  { pinVersion }: ConditionMentionSnapshotOptions = { pinVersion: true }
): ConditionMentionSnapshot {
  const versionId = diagnosis.toFhirResource().meta?.versionId
  const reference =
    pinVersion && versionId
      ? createReferenceVersionUrl(diagnosis.reference, versionId)
      : getReferenceString(diagnosis.reference)
  return { reference, name: diagnosis.name }
}
